import React from 'react';
import { EnvelopeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import { DataTable } from '@oysterjs/ui/data-table';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';

import {
  createEmailTemplateFilterDefinitions,
  type EmailTemplateFilter,
  filterEmailTemplates
} from '~/api/data/email-templates/email-template-filters';
import { Action, type EmailTemplateListFieldsFragment, Resource } from '~/api/graphql';
import { FormError } from '~/components/form-error';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { InlineNotice } from '~/components/inline-notice';
import { usePermissions } from '~/components/permissions';
import {
  type EmailTemplateSortKey,
  getEmailTemplateColumns,
  sortEmailTemplates
} from './email-template-columns';
import { EmailTemplateArchiveDialog } from './email-template-archive-dialog';
import { NEW_EMAIL_TEMPLATE_PATH, emailTemplatePath } from './email-template-paths';
import { useEmailTemplateActions } from './use-email-template-actions';

type EmailTemplate = EmailTemplateListFieldsFragment;

const VIEW_STORAGE_KEY = 'cara_email_templates_view';

export const EmailTemplatesList = ({ emailTemplates }: { emailTemplates: EmailTemplate[] }) => {
  const filterDefinitions = React.useMemo(() => createEmailTemplateFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<EmailTemplateFilter, EmailTemplateSortKey>(
      VIEW_STORAGE_KEY,
      filterDefinitions,
      { defaultSortDirection: 'desc' }
    );
  const actions = useEmailTemplateActions(emailTemplates);
  const canCreate = usePermissions().hasPermission(Resource.EmailTemplate, Action.Create);
  const newTemplateButton = canCreate && (
    <Button color="sky" href={NEW_EMAIL_TEMPLATE_PATH}>
      New email template
    </Button>
  );

  if (emailTemplates.length === 0) {
    return (
      <LibraryListEmptyState
        icon={EnvelopeIcon}
        title="No email templates yet"
        description="Templates let your team send the same well-written email every time. Cara fills in each client's details and writes the parts you mark for it."
        action={newTemplateButton}
      />
    );
  }

  const isUsageUnavailable = emailTemplates.some((template) => template.usageLast30Days == null);
  const matchingTemplates = filterEmailTemplates(emailTemplates, activeFilters);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-full sm:max-w-sm">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={initialFilters}
            ariaLabel="Search email templates"
            placeholder="Filter by name or date..."
          />
        </div>
        {newTemplateButton}
      </div>
      {isUsageUnavailable && (
        <InlineNotice
          tone="warning"
          title="We couldn't load usage numbers right now."
          description="Your templates are all here and working. Usage will show again after a refresh."
        />
      )}
      <FormError message={actions.failure} />
      {matchingTemplates.length === 0 ? (
        <LibraryListEmptyState
          icon={MagnifyingGlassIcon}
          title="No email templates match your search"
          description="Try adjusting your search or filters."
        />
      ) : (
        <div className="rounded-lg bg-white px-4 shadow sm:px-6 dark:bg-neutral-800">
          <DataTable
            data={matchingTemplates}
            columns={getEmailTemplateColumns(actions)}
            keyExtractor={(template) => template.id}
            rowHref={(template) => emailTemplatePath(template.id)}
            tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
            sortKey={sortBy}
            sortDirection={sortDirection}
            onSort={setSort}
            sortFn={sortEmailTemplates}
          />
        </div>
      )}
      <EmailTemplateArchiveDialog
        templateName={actions.archive.template?.name}
        open={!!actions.archive.template}
        failure={actions.archive.failure}
        isArchiving={actions.archive.isArchiving}
        onCancel={actions.archive.cancel}
        onConfirm={actions.archive.confirm}
      />
    </div>
  );
};
